// MyDetails — a person's own name, contact and timezone.
//
// The classic Profile screen carries these; the rebuilt Me screens had only a
// password change, so nobody on the new apps could correct their own phone
// number or address.
//
//   PUT /api/auth/profile { name, email, timezone, phone_number,
//                           address, city, state, zip_code }
//
// Deliberately NOT here: the SSN fields and the encryption passkey, which
// live in the classic Admin/Profile screens. CLAUDE.md rule 5 puts
// ssnEncryption.js and the auth module off-limits, and a screen that merely
// looks like it handles them is worse than one that does not offer them.
//
// The server allows an empty string to CLEAR a field, so this sends what the
// person typed rather than dropping blanks — clearing an old phone number has
// to be possible.
//
// ── The photo (added 2026-09-03) ────────────────────────────────────────────
//
// Its own two-step action, NOT part of the form, because the endpoints are
// two:
//
//   POST /api/uploads              multipart, field name `file`  → { url }
//   PUT  /api/auth/profile         { profile_picture: url }      → the user row
//
// `POST /api/uploads` is `authMiddleware` only — no role check — so anybody
// signed in may upload their own. `PUT /api/auth/profile` sets the column
// whenever `profile_picture !== undefined` and stores `'' → null`, which is
// what Remove sends. The photo is therefore saved the moment it uploads and
// does not wait for Save; `detailsBody` never carries profile_picture, so
// saving the form cannot clobber a photo and changing a photo cannot discard
// what the person is typing.
//
// There is NO optimistic preview. The classic screen shows an
// `URL.createObjectURL` blob straight away and reverts on failure, and a
// worker on bad signal therefore sees their new photo, then a refusal, then
// the old photo — which reads as "it saved, then broke". Here the picture on
// screen is always one the server has confirmed, and a refusal is a sentence
// beside the picture that did not change.

import React, { useEffect, useState } from 'react';
import { Sheet, FwButton, FwField, FwInput, FwSelect, Notice, initialsOf } from '../../ui/fw';
import { put, request, describeError, serverSentence } from './http';
import { rejectUpload, uploadForm } from './uploads';
import { useAuth } from '../../App';

// The same list the classic Profile offers. Kept short and US-centric because
// that is where the crews are; the server accepts any non-empty string.
export const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const FIELDS = ['name', 'email', 'phone_number', 'address', 'city', 'state', 'zip_code', 'timezone'];

export function seedDetails(user) {
  const out = {};
  for (const f of FIELDS) out[f] = user && user[f] != null ? String(user[f]) : '';
  if (!out.timezone) out.timezone = 'America/New_York';
  return out;
}

/** A sentence naming what is wrong, or null. */
export function validateDetails(form) {
  if (!form.name || !form.name.trim()) return 'Your name cannot be blank.';
  if (!form.email || !form.email.trim()) return 'Your email cannot be blank — it is how you sign in.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'That email does not look right.';
  return null;
}

/**
 * What PUT /api/auth/profile receives.
 *
 * Every field goes, INCLUDING empty ones: the handler treats '' as "clear
 * this", and dropping blanks would make an old phone number impossible to
 * remove. name and email are trimmed because they are identity.
 */
export function detailsBody(form) {
  const body = {};
  for (const f of FIELDS) body[f] = form[f] == null ? '' : String(form[f]);
  body.name = body.name.trim();
  body.email = body.email.trim();
  return body;
}

/**
 * The src for a stored `profile_picture`, or null.
 *
 * The column holds whatever was written to it over the app's life: a full
 * `http(s)://…`, a rooted `/uploads/x.png` (what POST /api/uploads answers
 * today), and — from older builds — a bare `uploads/x.png`. A bare one used as
 * a src resolves against the CURRENT path, so on /field/me it asks the server
 * for /field/uploads/x.png, gets index.html, and renders as a broken image.
 * Rooting it is the whole job.
 */
export function photoSrc(picture) {
  const raw = picture == null ? '' : String(picture).trim();
  if (!raw) return null;
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
  return raw.startsWith('/') ? raw : `/${raw}`;
}

/**
 * What PUT /api/auth/profile receives for the photo.
 *
 * A blank url is Remove: the handler stores `profile_picture || null`, so ''
 * clears the column. It must still be SENT — the handler only touches the
 * column when the key is present — which is the same trap as detailsBody.
 */
export function photoBody(url) {
  return { profile_picture: url == null ? '' : String(url) };
}

/** The url out of a POST /api/uploads answer, or null if it did not carry one. */
export function uploadedUrl(answer) {
  const url = answer && typeof answer.url === 'string' ? answer.url.trim() : '';
  return url || null;
}

export default function MyDetails({ open, user, onClose, onSaved }) {
  const [form, setForm] = useState(() => seedDetails(user));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The photo is its own action with its own busy flag and its own sentence,
  // so a failed upload never looks like a failed form save and vice versa.
  const [photo, setPhoto] = useState(() => photoSrc(user && user.profile_picture));
  const [photoBusy, setPhotoBusy] = useState('');
  const [photoError, setPhotoError] = useState('');
  // Only for refreshing the signed-in user after a photo changes, so every
  // avatar in the shell catches up. Optional: the Me screens' tests mock
  // useAuth without a login, and a missing one must not throw.
  const auth = useAuth();

  useEffect(() => {
    if (open) {
      setForm(seedDetails(user));
      setError('');
      setPhoto(photoSrc(user && user.profile_picture));
      setPhotoError('');
    }
  }, [open, user]);

  /**
   * Hand the shell the user row the server just returned, MERGED (#512).
   *
   * `PUT /api/auth/profile` answers without a `permissions` key, so handing it
   * to `login` — which replaces the user — dropped permissions and took every
   * useCan() to false until reload. `updateUser` merges instead. The `login`
   * fallback is kept for a shell that predates it, and is still better than
   * showing stale details; the merge is the path that runs.
   */
  const adopt = (saved) => {
    if (!saved) return;
    if (auth && typeof auth.updateUser === 'function') { auth.updateUser(saved); return; }
    let token = null;
    try { token = localStorage.getItem('token'); } catch (_) { /* private mode */ }
    if (auth && typeof auth.login === 'function' && token) auth.login(token, saved);
  };

  const onPickPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    // Cleared FIRST, before any return and before anything that awaits.
    //
    // A real <input type="file"> keeps its selection and fires NO change event
    // when the file chosen is the one it already holds, so every one of the
    // four ways out of this handler — no file, the rules below, a refused
    // upload, success — has to clear it or the worker cannot re-pick that
    // photo at all. The only escapes are a different photo or a reload,
    // neither of which anybody would guess. Shipped twice already: PR #518
    // fixed it across the classic screens, and it came back in the rebuilt
    // Settings.js written from scratch.
    e.target.value = '';
    if (!file) return;

    const refusal = rejectUpload(file);
    if (refusal) { setPhotoError(refusal); return; }

    setPhotoBusy('upload');
    setPhotoError('');
    try {
      const url = uploadedUrl(await request('/uploads', { method: 'POST', body: uploadForm(file) }));
      if (!url) throw new Error('The upload finished but sent no address back, so there is nothing to save.');
      const saved = await put('/auth/profile', photoBody(url));
      setPhoto(photoSrc((saved && saved.profile_picture) || url));
      adopt(saved);
      if (onSaved) await onSaved(saved);
    } catch (err) {
      // The server's own sentence when it sent one — serverSentence rather
      // than describeError, which flattens every 403 to "You do not have
      // permission to do that." and would hide "Only image files are
      // allowed!". The picture on screen is untouched: nothing was ever
      // optimistically swapped, so there is no broken image to fall back from.
      setPhotoError(serverSentence(err, 'That photo did not upload.'));
    } finally {
      setPhotoBusy('');
    }
  };

  const onRemovePhoto = async () => {
    setPhotoBusy('remove');
    setPhotoError('');
    try {
      const saved = await put('/auth/profile', photoBody(''));
      setPhoto(null);
      adopt(saved);
      if (onSaved) await onSaved(saved);
    } catch (err) {
      setPhotoError(serverSentence(err, 'That photo could not be removed.'));
    } finally {
      setPhotoBusy('');
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const bad = validateDetails(form);
    if (bad) { setError(bad); return; }
    setBusy(true);
    setError('');
    try {
      const saved = await put('/auth/profile', detailsBody(form));
      if (onSaved) await onSaved(saved);
      if (onClose) onClose();
    } catch (err) {
      setError(describeError(err, 'Could not save your details.'));
    } finally {
      setBusy(false);
    }
  };

  const formId = 'fw-my-details';
  return (
    <Sheet
      open={open}
      title="My details"
      onClose={onClose}
      footer={(
        <div className="fw-jobform__footer">
          <FwButton variant="ghost" onClick={onClose} disabled={busy}>Cancel</FwButton>
          <FwButton variant="primary" type="submit" form={formId} loading={busy}>Save</FwButton>
        </div>
      )}
    >
      <form id={formId} className="fw-form" onSubmit={submit} noValidate>
        {error ? <Notice tone="stop" title="Not saved">{error}</Notice> : null}

        <div className="fw-photo">
          {photo
            ? (
              <img
                className="fw-photo__img"
                src={photo}
                alt=""
                // A stored path that 404s (a file cleaned off the droplet, an
                // old bare path) would otherwise render as the browser's
                // broken-image glyph. Dropping to initials is the same answer
                // as having no photo, which is what is true.
                onError={() => setPhoto(null)}
              />
            )
            : <span className="fw-photo__initials" aria-hidden="true">{initialsOf(form.name || (user && user.name) || '')}</span>}
          <div className="fw-photo__actions">
            <label className="fw-photo__pick">
              <input
                id="md-photo"
                type="file"
                accept=".jpg,.jpeg,.png,.gif,.ico,.svg,image/*"
                onChange={onPickPhoto}
                disabled={!!photoBusy}
                aria-label="Choose a profile photo"
              />
              <span>{photoBusy === 'upload' ? 'Uploading…' : (photo ? 'Change photo' : 'Add a photo')}</span>
            </label>
            {photo ? (
              <FwButton variant="ghost" onClick={onRemovePhoto} loading={photoBusy === 'remove'} disabled={photoBusy === 'upload'}>
                Remove
              </FwButton>
            ) : null}
            <p className="fw-photo__hint">Saved as soon as it uploads. jpg, jpeg, png, gif, ico or svg, under 5 MB.</p>
          </div>
        </div>
        {photoError ? <Notice tone="stop" title="Photo not changed">{photoError}</Notice> : null}

        <FwField label="Name" htmlFor="md-name" required>
          <FwInput id="md-name" value={form.name} onChange={set('name')} autoComplete="name" />
        </FwField>
        <FwField label="Email" htmlFor="md-email" required hint="You sign in with this.">
          <FwInput id="md-email" type="email" value={form.email} onChange={set('email')} autoComplete="email" />
        </FwField>
        <FwField label="Phone" htmlFor="md-phone">
          <FwInput id="md-phone" type="tel" value={form.phone_number} onChange={set('phone_number')} autoComplete="tel" />
        </FwField>
        <FwField label="Address" htmlFor="md-address">
          <FwInput id="md-address" value={form.address} onChange={set('address')} autoComplete="street-address" />
        </FwField>
        <div className="fw-jobform__pair">
          <FwField label="City" htmlFor="md-city">
            <FwInput id="md-city" value={form.city} onChange={set('city')} autoComplete="address-level2" />
          </FwField>
          <FwField label="State" htmlFor="md-state">
            <FwInput id="md-state" value={form.state} onChange={set('state')} autoComplete="address-level1" maxLength={2} />
          </FwField>
        </div>
        <div className="fw-jobform__pair">
          <FwField label="ZIP" htmlFor="md-zip">
            <FwInput id="md-zip" value={form.zip_code} onChange={set('zip_code')} autoComplete="postal-code" inputMode="numeric" />
          </FwField>
          <FwField label="Timezone" htmlFor="md-tz" hint="Your hours are shown in this zone.">
            <FwSelect id="md-tz" value={form.timezone} onChange={set('timezone')}>
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz.replace('America/', '').replace('Pacific/', '').replace(/_/g, ' ')}</option>)}
            </FwSelect>
          </FwField>
        </div>
      </form>
    </Sheet>
  );
}
